import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { FileDown, ChevronRight, Download } from "lucide-react";
import { Card } from "./ui/card";
import { useIsMobile } from "./ui/use-mobile";

interface DownloadItem {
  id: number;
  file_name: string;
  file_url: string;
  file_desc: string | null;
  create_time: string;
}

export function DownloadSection() {
  const [downloads, setDownloads] = useState<DownloadItem[]>([]);
  const [loading, setLoading] = useState(true);
  const isMobile = useIsMobile();

  useEffect(() => {
    fetchDownloads();
  }, []);

  const fetchDownloads = async () => {
    try {
      // 获取最早创建的2条资料
      const resp = await fetch('/api/public/downloads?limit=2');
      const result = await resp.json();
      if (result.ok && result.data) {
        setDownloads(result.data);
      }
    } catch (err) {
      console.error('获取资料列表失败:', err);
    } finally {
      setLoading(false);
    }
  };

  // 处理下载
  const handleDownload = (item: DownloadItem, e: React.MouseEvent) => {
    e.stopPropagation();
    const link = document.createElement('a');
    link.href = item.file_url;
    link.download = item.file_name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="space-y-3">
      {/* Title section */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="w-1 h-6 bg-[#003c8c] rounded" />
          <h3 className={`text-gray-800 ${isMobile ? 'text-base' : ''}`}>资料下载</h3>
        </div>
        <Link
          to="/downloads"
          className={`text-[#003c8c] hover:text-[#0050b8] flex items-center ${isMobile ? 'text-xs' : 'text-sm'}`}
        >
          查看更多
          <ChevronRight className={`${isMobile ? 'w-3 h-3' : 'w-4 h-4'} ml-1`} />
        </Link>
      </div>

      {/* 资料列表 */}
      {loading ? (
        <div className={`text-center text-gray-400 ${isMobile ? 'py-4' : 'py-8'}`}>
          <p className={isMobile ? 'text-sm' : ''}>加载中...</p>
        </div>
      ) : downloads.length > 0 ? (
        <div className={`grid ${isMobile ? 'grid-cols-1 gap-2' : 'grid-cols-2 gap-3'}`}>
          {downloads.map((item) => (
            <Card
              key={item.id}
              className="bg-gray-50 border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 cursor-pointer rounded-lg"
            >
              {isMobile ? (
                /* Mobile Layout - Two rows */
                <div className="p-3 bg-white rounded-lg">
                  <div className="flex flex-col gap-2">
                    {/* Row 1: Title */}
                    <span className={`text-gray-700 text-center leading-tight ${isMobile ? 'text-xs' : 'text-sm'} line-clamp-2`}>
                      {item.file_name}
                    </span>
                    
                    {/* Row 2: Download Button (full width) */}
                    <button
                      onClick={(e) => handleDownload(item, e)}
                      style={{
                        width: '100%',
                        padding: isMobile ? '6px 12px' : '8px 16px',
                        backgroundColor: '#003c8c',
                        color: 'white',
                        fontSize: isMobile ? '11px' : '12px',
                        fontWeight: 500,
                        borderRadius: '6px',
                        border: 'none',
                        cursor: 'pointer',
                        transition: 'background-color 0.2s'
                      }}
                      onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#0050b8'}
                      onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#003c8c'}
                    >
                      下载
                    </button>
                  </div>
                </div>
              ) : (
                /* Desktop Layout */
                <div className="flex p-4 bg-white rounded-lg h-[80px] items-center">
                  <div className="flex items-center justify-between gap-2 w-full">
                    <span className="text-gray-700 text-sm flex-1 truncate">
                      {item.file_name}
                    </span>
                    <button
                      onClick={(e) => handleDownload(item, e)}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: '#003c8c',
                        color: 'white',
                        fontSize: '12px',
                        fontWeight: 500,
                        borderRadius: '6px',
                        border: 'none',
                        cursor: 'pointer',
                        flexShrink: 0,
                        transition: 'background-color 0.2s'
                      }}
                      onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#0050b8'}
                      onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#003c8c'}
                    >
                      下载
                    </button>
                  </div>
                </div>
              )}
            </Card>
          ))}
        </div>
      ) : (
        <div className={`text-center text-gray-400 ${isMobile ? 'py-4' : 'py-8'}`}>
          <Download className={`${isMobile ? 'w-8 h-8' : 'w-10 h-10'} mx-auto mb-2 opacity-50`} />
          <p className={isMobile ? 'text-sm' : ''}>暂无资料</p>
        </div>
      )}
    </div>
  );
}
